import { Extent } from 'ol/extent'
import * as React from 'react'
import { createPortal } from 'react-dom'
import { DatasetLayer, Layer, ModelOutputLayer } from '../state'
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid, TileGridProps, formatTileGridUnitLabel } from '../modelling/tile_grid'
import { ChartData, extentToChartDataCached } from '../analysis_panel_tools/subsection'
import { resolvePaletteForCategoricalLayer } from '../layers/categorical_layer_colors'
import { findComparableCategoricalLayers } from '../layers/categorical_layer_view'
import { GenerateChart } from '../analysis_panel_tools/charts'
import { SNAPSHOT_PANEL_WIDTH_PX, EXPANDED_CHART_PANEL_WIDTH_PX } from '../analysis_panel_tools/charts/chart_size'
import {
    DEFAULT_TABLE_MEAN_CHART_SETTINGS,
    type TableMeanChartSettings,
} from '../analysis_panel_tools/charts/table_row_mean'
import {
    startPanelResizeWest,
} from '../analysis_panel_tools/floating_panel'
import './analysis_panel.css'
import { getArea } from 'ol/sphere'
import { fromExtent } from 'ol/geom/Polygon'
import { SnapshotExtentData, TeamExtentData } from '../editor/project_editor'
import { getMedianCellSize } from '../modelling/components/cell_area_component'
import { fetchExtentInfo } from '../modelling/components/extents_component'
import { getExtentShape } from '../map/map_view'
import { getPolygonFromLayer } from '../utils/shape'
import { Data } from 'rete/types/core/data'

export type ChartType = "pie" | "hist" | "bar" | "tableMean" | "tableSpider" | "kde"

function isTableRowMeansChartType(chartType: ChartType | undefined): boolean {
    return chartType === "tableMean" || chartType === "tableSpider"
}

interface ChartProps {
    chartType: ChartType | undefined
    chartData: ChartData | undefined
    props: TileGridProps | undefined
    cellArea: number
    showStdDev?: boolean
    hideNoDataValues?: boolean
    compact?: boolean
    panelWidth?: number
    panelHeight?: number
    svgId?: string
    tableMeanCompareOptions?: Array<{ layerId: number; name: string }>
    tableMeanCompareLayerId?: number | null
    onTableMeanCompareLayerIdChange?: (layerId: number | null) => void
    tableMeanCompareChartData?: ChartData
    tableMeanOverlayLayerIds?: number[]
    onTableMeanOverlayLayerIdsChange?: (layerIds: number[]) => void
    tableMeanOverlayChartDataByLayerId?: Record<number, ChartData>
    tableMeanPrimaryLayerName?: string
    tableMeanSettings?: TableMeanChartSettings
    onTableMeanSettingsChange?: (patch: Partial<TableMeanChartSettings>) => void
}

const Chart = ({
    chartType,
    chartData,
    props,
    cellArea,
    hideNoDataValues,
    compact,
    panelWidth,
    panelHeight,
    svgId,
    tableMeanCompareOptions,
    tableMeanCompareLayerId,
    onTableMeanCompareLayerIdChange,
    tableMeanCompareChartData,
    tableMeanOverlayLayerIds,
    onTableMeanOverlayLayerIdsChange,
    tableMeanOverlayChartDataByLayerId,
    tableMeanPrimaryLayerName,
    tableMeanSettings,
    onTableMeanSettingsChange,
}: ChartProps) => {

    if (!chartType || !chartData) return <></>

    return <GenerateChart
        chartData={chartData}
        chartType={chartType}
        props={props}
        cellArea={cellArea}
        hideNoDataValues={hideNoDataValues}
        compact={compact}
        panelWidth={panelWidth}
        panelHeight={panelHeight}
        svgId={svgId}
        tableMeanCompareOptions={tableMeanCompareOptions}
        tableMeanCompareLayerId={tableMeanCompareLayerId}
        onTableMeanCompareLayerIdChange={onTableMeanCompareLayerIdChange}
        tableMeanCompareChartData={tableMeanCompareChartData}
        tableMeanOverlayLayerIds={tableMeanOverlayLayerIds}
        onTableMeanOverlayLayerIdsChange={onTableMeanOverlayLayerIdsChange}
        tableMeanOverlayChartDataByLayerId={tableMeanOverlayChartDataByLayerId}
        tableMeanPrimaryLayerName={tableMeanPrimaryLayerName}
        tableMeanSettings={tableMeanSettings}
        onTableMeanSettingsChange={onTableMeanSettingsChange}
    />
}

const CHART_TYPE_LABELS: Record<ChartType, string> = {
    pie: "Pie chart",
    bar: "Bar chart",
    hist: "Histogram",
    tableMean: "Table",
    tableSpider: "Spider diagram",
    kde: "Chart",
}

interface ChartExpandedModalProps {
    open: boolean
    onClose: () => void
    chartType: ChartType
    chartData: ChartData
    tileProps: TileGridProps | undefined
    cellArea: number
    hideNoDataValues: boolean
    tableMeanCompareOptions?: Array<{ layerId: number; name: string }>
    tableMeanCompareLayerId?: number | null
    onTableMeanCompareLayerIdChange?: (layerId: number | null) => void
    tableMeanCompareChartData?: ChartData
    tableMeanOverlayLayerIds?: number[]
    onTableMeanOverlayLayerIdsChange?: (layerIds: number[]) => void
    tableMeanOverlayChartDataByLayerId?: Record<number, ChartData>
    tableMeanPrimaryLayerName?: string
    tableMeanSettings?: TableMeanChartSettings
    onTableMeanSettingsChange?: (patch: Partial<TableMeanChartSettings>) => void
}

const ChartExpandedModal = ({
    open,
    onClose,
    chartType,
    chartData,
    tileProps,
    cellArea,
    hideNoDataValues,
    tableMeanCompareOptions,
    tableMeanCompareLayerId,
    onTableMeanCompareLayerIdChange,
    tableMeanCompareChartData,
    tableMeanOverlayLayerIds,
    onTableMeanOverlayLayerIdsChange,
    tableMeanOverlayChartDataByLayerId,
    tableMeanPrimaryLayerName,
    tableMeanSettings,
    onTableMeanSettingsChange,
}: ChartExpandedModalProps) => {
    React.useEffect(() => {
        if (!open) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose()
        }
        window.addEventListener("keydown", onKeyDown)
        return () => window.removeEventListener("keydown", onKeyDown)
    }, [open, onClose])

    if (!open) return null

    const expandedSvgId = `${chartType}-expanded`

    return createPortal(
        <div
            className="modal fade show d-block chart-expand-modal"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-xl modal-dialog-scrollable chart-expand-modal__dialog"
                role="document"
                onClick={event => event.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header py-2">
                        <h5 className="modal-title">{CHART_TYPE_LABELS[chartType]}</h5>
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body chart-expand-modal__body">
                        <Chart
                            chartType={chartType}
                            chartData={chartData}
                            props={tileProps}
                            cellArea={cellArea}
                            hideNoDataValues={hideNoDataValues}
                            compact={false}
                            panelWidth={EXPANDED_CHART_PANEL_WIDTH_PX}
                            svgId={expandedSvgId}
                            tableMeanCompareOptions={tableMeanCompareOptions}
                            tableMeanCompareLayerId={tableMeanCompareLayerId}
                            onTableMeanCompareLayerIdChange={onTableMeanCompareLayerIdChange}
                            tableMeanCompareChartData={tableMeanCompareChartData}
                            tableMeanOverlayLayerIds={tableMeanOverlayLayerIds}
                            onTableMeanOverlayLayerIdsChange={onTableMeanOverlayLayerIdsChange}
                            tableMeanOverlayChartDataByLayerId={tableMeanOverlayChartDataByLayerId}
                            tableMeanPrimaryLayerName={tableMeanPrimaryLayerName}
                            tableMeanSettings={tableMeanSettings}
                            onTableMeanSettingsChange={onTableMeanSettingsChange}
                        />
                    </div>
                    <div className="modal-footer py-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => downloadChartAsPNG(chartType, expandedSvgId)}
                        >
                            <i className="fas fa-download mr-1" /> Download
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

interface ChartSelectionProps {
    SourceType: string
    ChartTypeSelected: ChartType | undefined
    SetChartType: (type: ChartType) => void
    HideNoDataValues: boolean
    SetHideNoDataValues: (hide: boolean) => void
    hasTableRowMeans?: boolean
    compact?: boolean
    onExpand?: () => void
    canExpand?: boolean
}

function svgToDataUrl(svgContent: string): string {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`
}

function svgToImage(svg: SVGSVGElement): Promise<{ image: HTMLImageElement; width: number; height: number }> {
    const svgContent = new XMLSerializer().serializeToString(svg)
    const svgImage = new Image()
    svgImage.src = svgToDataUrl(svgContent)

    return new Promise((resolve, reject) => {
        svgImage.onload = () => {
            const width = svgImage.width || svg.width.baseVal.value || 0
            const height = svgImage.height || svg.height.baseVal.value || 0
            resolve({ image: svgImage, width, height })
        }
        svgImage.onerror = () => reject(new Error("Failed to render chart SVG"))
    })
}

function downloadChartAsPNG(ChartTypeSelected: ChartType | undefined, svgId?: string) {
    if (!ChartTypeSelected) return

    const primaryId = svgId ?? ChartTypeSelected
    let svg: SVGSVGElement | undefined
    for (const id of [primaryId, `${primaryId}-diff`]) {
        const el = document.getElementById(id)
        if (el instanceof SVGSVGElement) {
            svg = el
            break
        }
    }
    if (!svg) return

    void svgToImage(svg)
        .then(({ image, width, height }) => {
            const canvas = document.createElement("canvas")
            canvas.width = width
            canvas.height = height
            const context = canvas.getContext("2d")
            if (!context) return

            context.fillStyle = "#ffffff"
            context.fillRect(0, 0, width, height)
            context.drawImage(image, 0, 0, width, height)

            const url = canvas.toDataURL("image/png")
            const link = document.createElement("a")
            link.href = url
            link.download = `snapshot_${ChartTypeSelected}_${Date.now()}.png`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        })
        .catch(() => {
            // ignore failed export
        })
}

const ChartSelection = ({ SourceType, ChartTypeSelected, SetChartType, HideNoDataValues, SetHideNoDataValues, hasTableRowMeans, compact, onExpand, canExpand }: ChartSelectionProps) => {

    const typeArray = [...(ChartTypeArray.get(SourceType) || [])]
    if (hasTableRowMeans && SourceType === "CategoricalTileGrid") {
        typeArray.push("tableMean", "tableSpider")
    }

    const options = [
        { value: "pie", label: "Pie chart", icon: "fa-chart-pie", disabled: false },
        { value: "bar", label: "Bar chart", icon: "fa-chart-bar", disabled: false },
        { value: "hist", label: "Histogram", icon: "fa-chart-column", disabled: false },
        { value: "tableMean", label: "Table", icon: "fa-table", disabled: false },
        { value: "tableSpider", label: "Spider diagram", icon: "fa-spider", disabled: false },
    ].filter(option => (typeArray as string[]).includes(option.value))

    const iconClass = compact ? "fa-xs" : ""
    const wrapClass = compact
        ? "d-flex align-items-center justify-content-between mt-2 px-2"
        : "d-flex align-items-center justify-content-between mt-2 px-2"
    const sm = compact ? "btn-sm" : ""

    return (
        <div className={wrapClass} style={{ width: "100%" }}>
            <div className="btn-group">
                {options.map(option => (
                    <button 
                        key={`chart-option-${option.value}`}
                        disabled={option.disabled} 
                        title={option.label} 
                        type="button" 
                        onClick={() => SetChartType(option.value as ChartType)} 
                        className={`btn ${sm} ${ChartTypeSelected == option.value ? "btn-primary" : "btn-outline-primary"}`}
                    >
                        <i className={`fas ${option.icon} ${iconClass}`} />
                    </button>
                ))}
            </div>

            <div className="btn-group">
                {
                    SourceType !== "NumericTileGrid" && !isTableRowMeansChartType(ChartTypeSelected) && (
                        <button title='Filter missing data' onClick={() => SetHideNoDataValues(!HideNoDataValues)} type="button" className={`btn ${sm} btn-${!HideNoDataValues ? "outline-primary" : "primary"}`}><i className={`fas fa-filter ${iconClass}`}></i></button>
                    )
                }
                <button
                    title="Expand chart"
                    disabled={!canExpand}
                    onClick={onExpand}
                    type="button"
                    className={`btn ${sm} btn-outline-primary`}
                >
                    <i className={`fas fa-expand ${iconClass}`}></i>
                </button>
                <button title='Download' onClick={() => downloadChartAsPNG(ChartTypeSelected)} type="button" className={`btn ${sm} btn-outline-primary`}><i className={`fas fa-download ${iconClass}`}></i></button>
            </div>
        </div>
    );
}

function calculateFactor(chartData: ChartData){
    const totalCount = Array.from(chartData.count.values()).reduce((acc, count) => acc + count, 0)
    const noDataCount = Array.from(chartData.count.entries()).reduce((acc, [key, value]) => {
        const isNoData = typeof key === 'string' && key.trim().toLowerCase() === 'no data'
        return acc + (isNoData ? value : 0)
    }, 0)
    const denom = totalCount - noDataCount
    if (denom <= 0) return 1
    return totalCount / denom
}

interface ChartLegendProps {
    chartData: ChartData | undefined
    sourceType: string
    chartType: ChartType | undefined
    props: TileGridProps | undefined
    decimalPlaces: number
    data?: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null
    showStdDev: boolean
    setShowStdDev: (show: boolean) => void
    hideNoDataValues: boolean
    compact?: boolean
}

const NUMERIC_SECONDARY_STAT_KEYS = ["min", "max", "median", "range", "mean", "mode"] as const

const formatStatLabel = (key: string) => key[0].toUpperCase() + key.slice(1)

interface NumericStatRowProps {
    statKey: string
    label?: string
    value: number
    decimalPlaces: number
    perArea: boolean
    props: TileGridProps | undefined
    variant?: "sum" | "grid"
}

const NumericStatRow = ({ statKey, label, value, decimalPlaces, perArea, props, variant = "grid" }: NumericStatRowProps) => (
    <div className={`chart-legend-numeric-stat-row${variant === "sum" ? " chart-legend-numeric-stat-row--sum" : ""}`}>
        <label>{label ?? formatStatLabel(statKey)}</label>
        <input
            disabled
            type="text"
            className="chart-legend-numeric-stat-value"
            value={parseFloat(value.toFixed(decimalPlaces)).toLocaleString()}
        />
        <span className="chart-legend-numeric-stat-unit" title={formatTileGridUnitLabel(props, { perArea })}>
            {formatTileGridUnitLabel(props, { perArea })}
        </span>
    </div>
)

const ChartLegend = ({ chartData, sourceType, chartType, props, decimalPlaces, data, showStdDev, setShowStdDev, hideNoDataValues, compact }: ChartLegendProps) => {
    if (!chartData) {
        return null
    }

    let LegendItems

    if (isTableRowMeansChartType(chartType)) {
        return null
    } else if (sourceType !== "NumericTileGrid") {
        const factor = !hideNoDataValues ? 1 : calculateFactor(chartData)
        const totalCount = Array.from(chartData.count.values()).reduce((acc, count) => acc + count, 0)
        const sortedLegendItems = Array.from(chartData.count.entries()).sort((a, b) => b[1] - a[1])
        LegendItems = (
            <div className="chart-legend-badges">
                {sortedLegendItems.map(([label, count], index) => {
                    const labelStr = String(label)
                    const isNoData = labelStr.trim().toLowerCase() === 'no data'
                    const basePct = totalCount > 0 ? (count / totalCount) * 100 : 0
                    const adjPct = basePct * factor
                    const struck = isNoData && hideNoDataValues
                    return (
                        <span
                            key={`legend-item-${index}`}
                            className={`chart-legend-badge${struck ? " chart-legend-badge--strike" : ""}`}
                            title={labelStr}
                        >
                            <span
                                className="chart-legend-badge__swatch"
                                style={{ backgroundColor: `rgb(${chartData.colors?.get(label)?.slice(0, 3)?.join(",")})` }}
                            />
                            <span className="chart-legend-badge__label">{labelStr}</span>
                            <span className={`chart-legend-badge__pct${hideNoDataValues && !isNoData ? " chart-legend-badge__pct--struck" : ""}`}>
                                {basePct.toFixed(1)}%
                            </span>
                            {hideNoDataValues && !isNoData && (
                                <span className="chart-legend-badge__pct chart-legend-badge__pct--adj">
                                    {adjPct.toFixed(1)}%
                                </span>
                            )}
                        </span>
                    )
                })}
            </div>
        )
    } else {
        if (chartData.full_numeric_stats) {
            const NumStats = chartData.full_numeric_stats
            
            LegendItems = (
                <div className="chart-legend-numeric-stats">
                    {!showStdDev ? (
                        <>
                            <NumericStatRow
                                statKey="sum"
                                value={NumStats.sum}
                                decimalPlaces={decimalPlaces}
                                perArea={false}
                                props={props}
                                variant="sum"
                            />
                            <div className="chart-legend-numeric-stats-grid">
                                {NUMERIC_SECONDARY_STAT_KEYS.map(key => (
                                    <NumericStatRow
                                        key={`stat-${key}`}
                                        statKey={key}
                                        value={NumStats[key]}
                                        decimalPlaces={decimalPlaces}
                                        perArea={true}
                                        props={props}
                                    />
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <NumericStatRow
                                statKey="stdDevSum"
                                label="Sum"
                                value={NumStats.stdDevSum}
                                decimalPlaces={decimalPlaces}
                                perArea={false}
                                props={props}
                                variant="sum"
                            />
                            <div className="chart-legend-numeric-stats-grid">
                                <NumericStatRow
                                    statKey="stdDevMean"
                                    label="Mean"
                                    value={NumStats.stdDevMean}
                                    decimalPlaces={decimalPlaces}
                                    perArea={true}
                                    props={props}
                                />
                            </div>
                        </>
                    )}
                </div>
            )
        } else {
            LegendItems = ""
        }
    }


    return (
        <div className={`chart-legend${compact ? " chart-legend--compact" : ""}`}>
            {hideNoDataValues && sourceType !== "NumericTileGrid" && !isTableRowMeansChartType(chartType) && (
                <div className="chart-legend-note">Missing data points removed from distribution</div>
            )}
            {!hideNoDataValues && sourceType !== "NumericTileGrid" && !isTableRowMeansChartType(chartType) && (
                <div className="chart-legend-note">Missing data points included in distribution; labelled as "No data"</div>
            )}
            {LegendItems}
        </div>
    );
}

interface AnalysisPanelProps {
    setShowAP: () => void
    selectedArea: Extent | null
    setSelectedArea: (extent: Extent | null) => void
    selectedLayer: Layer | null
    layerStats: (layer: DatasetLayer | ModelOutputLayer) => BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null
    currentTab: number
    projectExtent: Extent
    ExtentList: TeamExtentData[]
    extentShape: SnapshotExtentData | undefined
    setExtentShape: (shape: SnapshotExtentData | undefined) => void
    projectExtentShape: SnapshotExtentData | undefined
    hideCoordinates?: boolean // Option to hide the coordinates box
    /** Fixed column width (px). Default 420 (map editor); embed uses ~248 to match compact sidebar */
    columnWidth?: number
    /** Embed: panel sits above layers in one column — no right border, flex-friendly height */
    embedStacked?: boolean
    model?: Data | null
    projectLayers?: Record<number, Layer>
    layerOrder?: number[]
    selectedLayerId?: number
}

let dataSourceType: string = "null"

const ChartTypeArray: Map<string, ChartType[]> = new Map()
ChartTypeArray.set("BooleanTileGrid", ["pie", "bar"])
ChartTypeArray.set("CategoricalTileGrid", ["pie", "bar"])
ChartTypeArray.set("NumericTileGrid", ["hist"])



export const AnalysisPanel = ({ selectedArea, setSelectedArea, setShowAP, selectedLayer, layerStats, currentTab, projectExtent, ExtentList, extentShape, setExtentShape, projectExtentShape, hideCoordinates = false, columnWidth = SNAPSHOT_PANEL_WIDTH_PX, embedStacked = false, model = null, projectLayers, layerOrder, selectedLayerId }: AnalysisPanelProps) => {

    const [panelWidth, setPanelWidth] = React.useState(columnWidth)
    const effectivePanelWidth = embedStacked ? columnWidth : panelWidth
    const panelBodyRef = React.useRef<HTMLDivElement>(null)
    const [panelHeight, setPanelHeight] = React.useState(0)

    React.useLayoutEffect(() => {
        const el = panelBodyRef.current
        if (!el) return
        const apply = () => setPanelHeight(el.clientHeight)
        apply()
        const observer = new ResizeObserver(apply)
        observer.observe(el)
        return () => observer.disconnect()
    }, [embedStacked])

    const [chartType, setChartType] = React.useState<ChartType>()
    const [chartData, setChartData] = React.useState<ChartData>()
    const [compareLayerId, setCompareLayerId] = React.useState<number | null>(null)
    const [compareChartData, setCompareChartData] = React.useState<ChartData | undefined>()
    const [overlayLayerIds, setOverlayLayerIds] = React.useState<number[]>([])
    const [overlayChartDataByLayerId, setOverlayChartDataByLayerId] = React.useState<Record<number, ChartData>>({})
    const [tableMeanSettings, setTableMeanSettings] = React.useState<TableMeanChartSettings>(DEFAULT_TABLE_MEAN_CHART_SETTINGS)
    const patchTableMeanSettings = React.useCallback((patch: Partial<TableMeanChartSettings>) => {
        setTableMeanSettings(prev => ({ ...prev, ...patch }))
    }, [])
    const [bins, setBins] = React.useState<number>(10) 
    const [colors, setColors] = React.useState<any>(null)
    const [decimalPlaces, setDecimalPlaces] = React.useState<number>(2)
    const [customBounds, setCustomBounds] = React.useState<[number, number]>([NaN, NaN])
    const [showStdDev, setShowStdDev] = React.useState<boolean>(false)
    const [hideNoDataValues, setHideNoDataValues] = React.useState<boolean>(false)
    const [chartExpanded, setChartExpanded] = React.useState<boolean>(false)
    const [coordinatesCollapsed, setCoordinatesCollapsed] = React.useState<boolean>(false)

    React.useEffect(() => {
        setChartExpanded(false)
        setCompareLayerId(null)
        setOverlayLayerIds([])
        setTableMeanSettings(DEFAULT_TABLE_MEAN_CHART_SETTINGS)
    }, [chartType, selectedLayer, selectedArea])

    let errorMsg: string = ""
    let showChart: boolean = false
    let data: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null = null

    React.useEffect(() => {    
        if (data !== null && selectedArea && (selectedLayer?.type == "ModelOutputLayer" || selectedLayer?.type == "DatasetLayer")) {
            const datasetLayer = selectedLayer as DatasetLayer
            setColors(data instanceof NumericTileGrid ? datasetLayer.fill : datasetLayer.colors)
        }
    }, [selectedLayer])

    React.useEffect(() => {
        if (data instanceof NumericTileGrid) {
            setShowStdDev(data.properties?.type === "Standard Deviation")
        }
    }, [data, selectedLayer])

    React.useEffect(() => {

        if (data !== null && selectedArea && (selectedLayer?.type == "ModelOutputLayer" || selectedLayer?.type == "DatasetLayer")) {

            const chartColors = data instanceof CategoricalTileGrid
                ? resolvePaletteForCategoricalLayer(data, selectedLayer)
                : selectedLayer.colors

            extentToChartDataCached(chartColors, data, selectedArea, selectedLayer.fill, bins, customBounds, extentShape, model, selectedLayer).then(chartData => {
                setChartData(chartData)
            })

            let dataType: string | undefined = undefined

            if (data instanceof BooleanTileGrid) {
                dataType = "BooleanTileGrid"
            } else if (data instanceof NumericTileGrid) {
                dataType = "NumericTileGrid"
                const newBounds: [number, number] = selectedLayer.overrideBounds && selectedLayer.bounds ? selectedLayer.bounds as [number, number] : [NaN, NaN]
                if ((JSON.stringify(newBounds) !== JSON.stringify(customBounds) && (newBounds[0] < newBounds[1] || isNaN(newBounds[0])))) {
                    setCustomBounds(newBounds)
                }
            } else if (data instanceof CategoricalTileGrid) {
                dataType = "CategoricalTileGrid"
            }
            
            if (dataType !== undefined) {
                if (dataSourceType !== dataType || chartType === undefined) {
                    dataSourceType = dataType
                    const charts = ChartTypeArray.get(dataType)
                    if (charts) setChartType(charts[0])
                }
            }
        } else {
            setChartData(undefined)
        }

    }, [selectedArea, selectedLayer, data, currentTab, bins, colors, customBounds, extentShape, model])

    React.useEffect(() => {
        if (isTableRowMeansChartType(chartType) && (!chartData?.tableRowMeansTables?.length && !chartData?.tableRowMeans?.length)) {
            setChartType("pie")
        }
    }, [chartData, chartType])

    const hasTableRowMeans = (chartData?.tableRowMeansTables?.length ?? 0) > 0
        || (chartData?.tableRowMeans?.length ?? 0) > 0

    const currentCategoricalGrid = React.useMemo(() => {
        if (!selectedLayer || (selectedLayer.type !== "ModelOutputLayer" && selectedLayer.type !== "DatasetLayer")) {
            return null
        }
        const grid = layerStats(selectedLayer)
        return grid instanceof CategoricalTileGrid ? grid : null
    }, [selectedLayer, layerStats])

    const comparableLayers = React.useMemo(() => {
        if (!currentCategoricalGrid || !projectLayers || !layerOrder) return []
        return findComparableCategoricalLayers(
            projectLayers,
            layerOrder,
            selectedLayerId,
            currentCategoricalGrid,
            layerStats
        )
    }, [currentCategoricalGrid, projectLayers, layerOrder, selectedLayerId, layerStats])

    const tableMeanCompareOptions = React.useMemo(
        () => comparableLayers.map(layer => ({ layerId: layer.layerId, name: layer.name })),
        [comparableLayers]
    )

    React.useEffect(() => {
        if (compareLayerId !== null && !tableMeanCompareOptions.some(option => option.layerId === compareLayerId)) {
            setCompareLayerId(null)
        }
    }, [compareLayerId, tableMeanCompareOptions])

    React.useEffect(() => {
        const validIds = new Set(tableMeanCompareOptions.map(option => option.layerId))
        setOverlayLayerIds(prev => {
            const next = prev.filter(id => validIds.has(id))
            return next.length === prev.length ? prev : next
        })
    }, [tableMeanCompareOptions])

    React.useEffect(() => {
        if (
            compareLayerId === null
            || !selectedArea
            || chartType !== "tableMean"
            || !(selectedLayer?.type === "ModelOutputLayer" || selectedLayer?.type === "DatasetLayer")
        ) {
            setCompareChartData(undefined)
            return
        }

        const compareLayer = comparableLayers.find(layer => layer.layerId === compareLayerId)?.layer
        if (!compareLayer) {
            setCompareChartData(undefined)
            return
        }

        const compareGrid = layerStats(compareLayer)
        if (!(compareGrid instanceof CategoricalTileGrid)) {
            setCompareChartData(undefined)
            return
        }

        let cancelled = false
        const compareColors = resolvePaletteForCategoricalLayer(compareGrid, compareLayer)

        extentToChartDataCached(
            compareColors,
            compareGrid,
            selectedArea,
            compareLayer.fill,
            bins,
            customBounds,
            extentShape,
            model,
            compareLayer
        ).then(nextChartData => {
            if (!cancelled) setCompareChartData(nextChartData)
        })

        return () => {
            cancelled = true
        }
    }, [
        compareLayerId,
        selectedArea,
        chartType,
        selectedLayer,
        comparableLayers,
        layerStats,
        bins,
        customBounds,
        extentShape,
        model,
    ])

    React.useEffect(() => {
        if (
            overlayLayerIds.length === 0
            || !selectedArea
            || chartType !== "tableSpider"
            || !(selectedLayer?.type === "ModelOutputLayer" || selectedLayer?.type === "DatasetLayer")
        ) {
            setOverlayChartDataByLayerId({})
            return
        }

        let cancelled = false
        const requestedIds = [...overlayLayerIds]

        Promise.all(
            requestedIds.map(async layerId => {
                const compareLayer = comparableLayers.find(layer => layer.layerId === layerId)?.layer
                if (!compareLayer) return null

                const compareGrid = layerStats(compareLayer)
                if (!(compareGrid instanceof CategoricalTileGrid)) return null

                const compareColors = resolvePaletteForCategoricalLayer(compareGrid, compareLayer)
                const nextChartData = await extentToChartDataCached(
                    compareColors,
                    compareGrid,
                    selectedArea,
                    compareLayer.fill,
                    bins,
                    customBounds,
                    extentShape,
                    model,
                    compareLayer
                )
                return { layerId, chartData: nextChartData }
            })
        ).then(results => {
            if (cancelled) return
            const next: Record<number, ChartData> = {}
            for (const result of results) {
                if (result) next[result.layerId] = result.chartData
            }
            setOverlayChartDataByLayerId(next)
        })

        return () => {
            cancelled = true
        }
    }, [
        overlayLayerIds,
        selectedArea,
        chartType,
        selectedLayer,
        comparableLayers,
        layerStats,
        bins,
        customBounds,
        extentShape,
        model,
    ])


    const [areaSummary, setAreaSummary] = React.useState<string>("0 km²")

    React.useEffect(() => {
        // Case 1: no selectedArea
        if (!selectedArea) {
            setAreaSummary("0 km²")
            return
        }

        // Case 2: selectedArea but no extentShape – use geometric area of the extent
        if (!extentShape) {
            const geomAreaKm2 = getArea(fromExtent(selectedArea)) / 1000000
            const formatted = geomAreaKm2.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
            setAreaSummary(`${formatted} km²`)
            return
        }

        // Case 3: both selectedArea and extentShape – prefer area from the shape geometry
        if (extentShape.shape) {
            const polygon = getExtentShape(extentShape.shape)
            if (polygon) {
                const shapeAreaKm2 = getArea(polygon) / 1000000
                const formatted = shapeAreaKm2.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })
                setAreaSummary(`${formatted} km²`)
                return
            }
        }

        // Case 4: extentShape defined by CQL/layer – fetch polygons from WFS (cached) and derive area
        if (extentShape.cql && extentShape.layer) {
            ;(async () => {
                try {
                    const polygons = await getPolygonFromLayer(extentShape)
                    if (!polygons || polygons.length === 0) {
                        setAreaSummary("0 km²")
                        return
                    }
                    const totalAreaM2 = polygons.reduce((acc, poly) => acc + getArea(poly), 0)
                    const totalAreaKm2 = totalAreaM2 / 1000000
                    const formatted = totalAreaKm2.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })
                    setAreaSummary(`${formatted} km²`)
                } catch {
                    setAreaSummary("0 km²")
                }
            })()
            return
        }

        // Fallback if extentShape exists but we couldn't derive a shape
        setAreaSummary("0 km²")
    }, [selectedArea, extentShape])

    if (selectedArea === null) {
        const isMac = navigator.platform.toUpperCase().indexOf('MAC')>=0
        errorMsg = `${isMac ? "Press the command key and drag the mouse to select an area." : "Press the control key and drag the mouse to select an area."}`
    } else if (selectedLayer === null) {
        errorMsg = "Please select a suitable layer."
    } else if (selectedLayer.type !== "DatasetLayer" && selectedLayer.type !== "ModelOutputLayer") {
        errorMsg = "Unsuitable layer type, please select a model output or dataset layer."
    } else {
        data = layerStats(selectedLayer)
        if (data === null) {
            errorMsg = "Model is not yet available."
        } else {
            showChart = true
        }
    }

    const panelInner = (
        <div ref={panelBodyRef} className="d-flex flex-column h-100" style={{ minHeight: 0, height: "100%" }}>
            <div className="flex-grow-1 analysis-panel-scroll" style={{ overflowY: "auto", overflowX: "hidden", flexBasis: "0px", minHeight: 0, backgroundColor: "white" }}>

                {
                    errorMsg.split("drag the mouse").length > 1 &&
                    <i className="fas fa-mouse-pointer" style={{ fontSize: "2em", display: "block", textAlign: "center", padding: 20, paddingTop: 50 }} />
                }
                {
                    errorMsg.split("select a suitable layer").length > 1 &&
                    <i className="fas fa-hand-pointer" style={{ fontSize: "2em", display: "block", textAlign: "center", padding: 20, paddingTop: 50 }} />
                }
                {
                    errorMsg.split("Model is not yet available").length > 1 &&
                    <i className="fas fa-spinner" style={{ fontSize: "2em", display: "block", textAlign: "center", padding: 20, paddingTop: 50 }} />
                }
                {
                    errorMsg.split("Unsuitable layer type").length > 1 &&
                    <i className="fas fa-exclamation-triangle" style={{ fontSize: "2em", display: "block", textAlign: "center", padding: 20, paddingTop: 50 }} />
                }
                {
                    !!errorMsg &&
                    <div style={{ textAlign: "center", padding: 30 }}>{errorMsg}</div>
                }

                {
                    showChart &&
                    <>
                        <ChartSelection
                            SourceType={dataSourceType}
                            ChartTypeSelected={chartType}
                            SetChartType={setChartType}
                            HideNoDataValues={hideNoDataValues}
                            SetHideNoDataValues={setHideNoDataValues}
                            hasTableRowMeans={hasTableRowMeans}
                            compact={embedStacked}
                            onExpand={() => setChartExpanded(true)}
                            canExpand={!!chartType && !!chartData}
                        />
                        <div className="analysis-panel-chart-wrap">
                            <Chart
                                chartType={chartType}
                                chartData={chartData}
                                props={data instanceof NumericTileGrid ? data.properties : undefined}
                                cellArea={data ? getMedianCellSize(data).area : 0}
                                showStdDev={showStdDev}
                                hideNoDataValues={hideNoDataValues}
                                compact={embedStacked}
                                panelWidth={effectivePanelWidth}
                                panelHeight={panelHeight}
                                tableMeanCompareOptions={tableMeanCompareOptions}
                                tableMeanCompareLayerId={compareLayerId}
                                onTableMeanCompareLayerIdChange={setCompareLayerId}
                                tableMeanCompareChartData={compareChartData}
                                tableMeanOverlayLayerIds={overlayLayerIds}
                                onTableMeanOverlayLayerIdsChange={setOverlayLayerIds}
                                tableMeanOverlayChartDataByLayerId={overlayChartDataByLayerId}
                                tableMeanPrimaryLayerName={selectedLayer?.name}
                                tableMeanSettings={tableMeanSettings}
                                onTableMeanSettingsChange={patchTableMeanSettings}
                            />
                        </div>
                        <div
                            className="analysis-panel-legend-wrap"
                            style={{
                                textAlign: embedStacked && !isTableRowMeansChartType(chartType) ? "left" : "center",
                                width: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                paddingLeft: embedStacked ? 4 : 8,
                                paddingRight: embedStacked ? 4 : 8,
                            }}
                        >
                            <div
                                style={
                                    embedStacked
                                        ? { display: "flex", flexDirection: "column", gap: 8, alignItems: "stretch", width: "100%" }
                                        : undefined
                                }
                            >
                                {
                                    chartType == "hist"
                                    &&
                                    <div className={embedStacked ? "d-flex align-items-center flex-wrap" : undefined} style={embedStacked ? { gap: 6 } : undefined}>
                                        <label style={embedStacked ? { marginBottom: 0, flex: "0 0 auto", fontSize: "0.8rem" } : { width: 40 }}>Bars</label>
                                        <input
                                            style={embedStacked ? { width: 52, flex: "0 0 auto" } : { width: 50 }}
                                            type="number"
                                            value={bins}
                                            onChange={(e) => setBins(+e.target.value)}
                                            min={1}
                                        />
                                    </div>
                                }
                                {
                                    data instanceof NumericTileGrid
                                    &&
                                    <div className={embedStacked ? "d-flex align-items-center flex-wrap" : undefined} style={embedStacked ? { gap: 6 } : undefined}>
                                        <label style={embedStacked ? { marginBottom: 0, flex: "0 1 auto", fontSize: "0.8rem", minWidth: 0 } : { width: 130 }}>Decimal places</label>
                                        <input
                                            style={embedStacked ? { width: 48, flex: "0 0 auto" } : { width: 50 }}
                                            type="number"
                                            value={decimalPlaces}
                                            onChange={(e) => setDecimalPlaces(+e.target.value)}
                                            min={0}
                                        />
                                    </div>
                                }
                            </div>
                            <ChartLegend
                                chartData={chartData}
                                sourceType={dataSourceType}
                                chartType={chartType}
                                props={data instanceof NumericTileGrid ? data.properties : undefined}
                                decimalPlaces={decimalPlaces}
                                data={data}
                                showStdDev={showStdDev}
                                setShowStdDev={setShowStdDev}
                                hideNoDataValues={hideNoDataValues}
                                compact={embedStacked}
                            />
                        </div>
                    </>
                }

            </div>

            {!hideCoordinates && (
              <>
                <div className="analysis-panel-coordinates-footer flex-shrink-0">
                <div className="px-2 py-2 border-top border-bottom bg-light d-flex align-items-center analysis-panel-coordinates-header">
                    <button
                        type="button"
                        className="analysis-panel-coordinates-toggle"
                        aria-expanded={!coordinatesCollapsed}
                        aria-controls="analysis-panel-coordinates-body"
                        aria-label="Selected coordinates"
                        onClick={() => setCoordinatesCollapsed(collapsed => !collapsed)}
                    >
                        {!coordinatesCollapsed && (
                            <span className="analysis-panel-coordinates-title">Selected coordinates</span>
                        )}
                        <span className="badge analysis-panel-epsg-badge">EPSG:3857</span>
                        {coordinatesCollapsed && (
                            <span className="badge analysis-panel-area-badge">{areaSummary}</span>
                        )}
                    </button>
                    {coordinatesCollapsed && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary analysis-panel-set-extent-btn"
                            data-toggle="modal"
                            data-target="#extentModal"
                        >
                            Set
                        </button>
                    )}
                    <button
                        type="button"
                        className="analysis-panel-coordinates-chevron-btn"
                        tabIndex={-1}
                        aria-hidden="true"
                        onClick={() => setCoordinatesCollapsed(collapsed => !collapsed)}
                    >
                        <i className={`fas fa-fw fa-chevron-${coordinatesCollapsed ? "up" : "down"} text-dark`} />
                    </button>
                </div>

                {!coordinatesCollapsed && (
                <div id="analysis-panel-coordinates-body" className="px-2 py-2 bg-white analysis-panel-coordinates">

                <div className="analysis-panel-area-row">
                    <label className="analysis-panel-area-label">Area</label>
                    <div className="analysis-panel-area-input-wrap">
                        <input
                            className="analysis-panel-area-input"
                            disabled
                            type="text"
                            value={areaSummary}
                        />
                        {selectedArea && !extentShape && (
                            <i
                                className="fas fa-vector-square analysis-panel-area-icon"
                                title="Area derived from bounding box"
                            />
                        )}
                        {extentShape && (
                            <i
                                className="fas fa-draw-polygon analysis-panel-area-icon"
                                title="Area derived from polygon, not bounding box"
                            />
                        )}
                    </div>
                    <div className="analysis-panel-coord-actions">
                        <div className="btn-group" data-toggle="modal" data-target="#extentModal">
                            <button className="btn btn-sm btn-outline-primary">
                                <i className="fas fa-square" /> Set extent
                            </button>
                        </div>
                        <div className="btn-group" onClick={()=>{navigator.clipboard.writeText(selectedArea ? selectedArea.join(",") : ""); alert("Copied selected area to clipboard.")}}>
                            <button className="btn btn-sm btn-outline-primary">
                                <i className="fas fa-copy" /> Copy extent
                            </button>
                        </div>
                    </div>
                </div>

                <div className="analysis-panel-extent-grid">
                    <div className="analysis-panel-extent-field">
                        <label>Xmin</label>
                        <input
                            disabled
                            type="text"
                            value={selectedArea ? selectedArea[0] : 0}
                        />
                    </div>
                    <div className="analysis-panel-extent-field">
                        <label>Ymin</label>
                        <input
                            disabled
                            type="text"
                            value={selectedArea ? selectedArea[1] : 0}
                        />
                    </div>
                    <div className="analysis-panel-extent-field">
                        <label>Xmax</label>
                        <input
                            disabled
                            type="text"
                            value={selectedArea ? selectedArea[2] : 0}
                        />
                    </div>
                    <div className="analysis-panel-extent-field">
                        <label>Ymax</label>
                        <input
                            disabled
                            type="text"
                            value={selectedArea ? selectedArea[3] : 0}
                        />
                    </div>
                </div>

                </div>
                )}
            <div className="modal fade" id="extentModal" role="dialog" aria-labelledby="extentModalTitle" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered extent-modal-dialog" role="document">
                    <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="extentModalTitle">Select Extent</h5>
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="extent-modal-form">
                                <input
                                    id="extent-input"
                                    type="text"
                                    placeholder='min x, min y, max x, max y'
                                    onChange={(e) => {
                                        let valid = false
                                        const extent = e.target.value.split(',').map(Number)
                                        valid = extent.length === 4 && extent.every((coord) => !isNaN(coord)) && extent[0] < extent[2] && extent[1] < extent[3]

                                        document.getElementById('extent-input-shape')!.setAttribute('value', "");
                                        (document.getElementById('extent-select') as HTMLSelectElement).value = "null"

                                        if(!valid){
                                            (document.getElementById('save-snapshot-extent-btn') as HTMLButtonElement).disabled = true
                                        }else{
                                            (document.getElementById('save-snapshot-extent-btn') as HTMLButtonElement).disabled = false
                                        }
                                    }}
                                />
                                <select onChange={async (e) => 
                                    {
                                        const selectedIndex = Number(e.target.value)
                                        const selectedExtent = ExtentList.find(extent => extent.id === selectedIndex)
                                        if (selectedExtent) {
                                            document.getElementById('extent-input')!.setAttribute('value', selectedExtent.value.join(','))
                                            const info = await fetchExtentInfo(selectedExtent.id)
                                            const shape = info.shape ? JSON.stringify(info.shape) : undefined
                                            document.getElementById('extent-input-shape')!.setAttribute('value', shape ?? "")
                                            document.getElementById('extent-input-layer')!.setAttribute('value', selectedExtent.layer ?? "")
                                            document.getElementById('extent-input-cql')!.setAttribute('value', selectedExtent.cql ?? "")
                                            document.getElementById('extent-input-region')!.setAttribute('value', selectedExtent.region ?? "")
                                            document.getElementById('extent-input-external-link')!.setAttribute('value', selectedExtent.externalLink ?? "")
                                        }
                                    }}
                                    className='form-select form-select-sm' id="extent-select">
                                <option value=''>Select project extent</option>
                                {ExtentList.map((extent) => (
                                    <option key={extent.id} value={extent.id}>{extent.name}</option>
                                ))}
                            </select>
                                
                                <input type="text" id="extent-input-shape" placeholder="Extent shape" hidden={true} />
                                <input type="text" id="extent-input-layer" placeholder="Extent Layer" hidden={true} />
                                <input type="text" id="extent-input-cql" placeholder="Extent CQL" hidden={true} />
                                <input type="text" id="extent-input-region" placeholder="Extent URL" hidden={true} />
                                <input type="text" id="extent-input-external-link" placeholder="Extent External Link" hidden={true} />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-dismiss="modal">Close</button>

                        <button type='button' className='btn btn-primary' data-dismiss="modal" onClick={()=>{
                            setSelectedArea(projectExtent)
                            setExtentShape(projectExtentShape)
                            setTimeout(() => {
                                window.dispatchEvent(new Event('map:fitToView'))
                            }, 150)
                        }}>Use project extent</button>
                        <button 
                            id="save-snapshot-extent-btn" 
                            type="button" 
                            data-dismiss="modal"
                            className="btn btn-primary"
                            onClick={()=>{
                                const extent = (document.getElementById('extent-input') as HTMLInputElement).value.split(',').map(Number)
                                if(extent.length === 4 && extent.every((coord) => !isNaN(coord)) && extent[0] < extent[2] && extent[1] < extent[3]){
                                    const extentFeature = fromExtent(extent)
                                    const extentArea = getArea(extentFeature)
                                    if(extentArea > 0){
                                        setSelectedArea(extent)
                                        const shape = document.getElementById('extent-input-shape')!.getAttribute('value') as string
                                        const snapshotExtentData: SnapshotExtentData = {
                                            value: extent,
                                            shape: shape ? JSON.parse(shape) : undefined,
                                            layer: document.getElementById('extent-input-layer')!.getAttribute('value') ?? undefined,
                                            cql: document.getElementById('extent-input-cql')!.getAttribute('value') ?? undefined,
                                            region: document.getElementById('extent-input-region')!.getAttribute('value') ?? undefined,
                                            externalLink: document.getElementById('extent-input-external-link')!.getAttribute('value') ?? undefined
                                        }
                                        setExtentShape(snapshotExtentData)
                                        setTimeout(() => {
                                            window.dispatchEvent(new Event('map:fitToView'))
                                        }, 150)
                                    }
                                }
                            }}
                            >Set as snapshot extent</button>
                        </div>          
                    </div>
                </div>
            </div>
                </div>
              </>
            )}
            {showChart && chartType && chartData && (
                <ChartExpandedModal
                    open={chartExpanded}
                    onClose={() => setChartExpanded(false)}
                    chartType={chartType}
                    chartData={chartData}
                    tileProps={data instanceof NumericTileGrid ? data.properties : undefined}
                    cellArea={data ? getMedianCellSize(data).area : 0}
                    hideNoDataValues={hideNoDataValues}
                    tableMeanCompareOptions={tableMeanCompareOptions}
                    tableMeanCompareLayerId={compareLayerId}
                    onTableMeanCompareLayerIdChange={setCompareLayerId}
                    tableMeanCompareChartData={compareChartData}
                    tableMeanOverlayLayerIds={overlayLayerIds}
                    onTableMeanOverlayLayerIdsChange={setOverlayLayerIds}
                    tableMeanOverlayChartDataByLayerId={overlayChartDataByLayerId}
                    tableMeanPrimaryLayerName={selectedLayer?.name}
                    tableMeanSettings={tableMeanSettings}
                    onTableMeanSettingsChange={patchTableMeanSettings}
                />
            )}
        </div>
    )

    if (embedStacked) {
        return (
            <div
                className="bg-light d-flex flex-column analysis-panel-embed"
                style={{
                    minWidth: columnWidth,
                    maxWidth: columnWidth,
                    width: "100%",
                    minHeight: 0,
                    flex: "1 1 auto",
                    height: "100%",
                    overflow: "hidden",
                }}
            >
                <div
                    className="px-2 py-1 border-top border-bottom d-flex align-items-center justify-content-between"
                    style={{ fontSize: "0.8125rem" }}
                >
                    Snapshot tool
                    <i
                        className="fas fa-times"
                        style={{ cursor: "pointer", fontSize: "0.85em" }}
                        onClick={setShowAP}
                    />
                </div>
                {panelInner}
            </div>
        )
    }

    return (
        <div
            className="bg-light d-flex flex-column border-right analysis-panel-docked"
            style={{
                position: "relative",
                width: panelWidth,
                minWidth: panelWidth,
                maxWidth: panelWidth,
                height: "100%",
                flexShrink: 0,
                overflow: "hidden",
                minHeight: 0,
            }}
        >
            <div
                className="analysis-panel-resize-w"
                aria-hidden="true"
                onPointerDown={event => startPanelResizeWest(event, panelWidth, setPanelWidth)}
            />
            <div className="px-2 py-2 border-top border-bottom d-flex align-items-center justify-content-between">
                Snapshot tool
                <i
                    className="fas fa-times"
                    style={{ cursor: "pointer" }}
                    onClick={setShowAP}
                />
            </div>
            {panelInner}
        </div>
    )
}